import React, { useState, useRef, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';

const SearchBar = ({ onSearch, searchQuery, isLoading }) => {
  const [inputValue, setInputValue] = useState(searchQuery || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Predefined suggestions for autocomplete
  const predefinedSuggestions = [
    'DJ Paris', 'Musicien jazz', 'Chanteur pop', 'Agent musical',
    'Producteur hip-hop', 'Guitariste rock', 'Pianiste classique',
    'Batteur expérimenté', 'Bassiste professionnel', 'Violoniste',
    'Saxophoniste jazz', 'Trompettiste', 'Agent de booking',
    'Manager artistique', 'Sound designer', 'Compositeur',
    'Arrangeur musical', 'Ingénieur du son', 'Mixeur audio',
    'Artiste électro', 'Groupe de musique', 'Orchestra',
    'Quatuor à cordes', 'Trio jazz', 'Duo acoustique'
  ];

  // Update input when searchQuery prop changes
  useEffect(() => {
    setInputValue(searchQuery || '');
  }, [searchQuery]);

  // Generate suggestions based on input
  const generateSuggestions = (value) => {
    if (!value?.trim()) {
      setSuggestions([]);
      return;
    }

    const filtered = predefinedSuggestions?.filter(suggestion =>
      suggestion?.toLowerCase()?.includes(value?.toLowerCase())
    );

    // Add the current input as a suggestion if it's not empty
    const suggestions = value?.trim() ? [value?.trim(), ...filtered?.slice(0, 7)] : filtered?.slice(0, 8);
    
    // Remove duplicates
    const uniqueSuggestions = [...new Set(suggestions)];
    setSuggestions(uniqueSuggestions?.slice(0, 8));
  };

  // Handle input change
  const handleInputChange = (e) => {
    const value = e?.target?.value;
    setInputValue(value);
    setSelectedSuggestionIndex(-1);
    generateSuggestions(value);
    setShowSuggestions(true);
  };

  // Handle input focus
  const handleInputFocus = () => {
    generateSuggestions(inputValue);
    setShowSuggestions(true);
  };

  // Handle input blur
  const handleInputBlur = (e) => {
    // Delay hiding suggestions to allow for click events
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }, 150);
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e?.preventDefault();
    const query = inputValue?.trim();
    if (query || suggestions?.length > 0) {
      const searchTerm = selectedSuggestionIndex >= 0 && suggestions?.[selectedSuggestionIndex] 
        ? suggestions?.[selectedSuggestionIndex] 
        : query;
      onSearch?.(searchTerm);
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions?.length === 0) {
      if (e?.key === 'Enter') {
        handleSubmit(e);
      }
      return;
    }

    switch (e?.key) {
      case 'ArrowDown':
        e?.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < suggestions?.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e?.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev > 0 ? prev - 1 : suggestions?.length - 1
        );
        break;
      case 'Enter':
        e?.preventDefault();
        if (selectedSuggestionIndex >= 0 && suggestions?.[selectedSuggestionIndex]) {
          const selectedSuggestion = suggestions?.[selectedSuggestionIndex];
          setInputValue(selectedSuggestion);
          onSearch?.(selectedSuggestion);
        } else {
          handleSubmit(e);
        }
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        inputRef?.current?.blur();
        break;
      default:
        break;
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    onSearch?.(suggestion);
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    inputRef?.current?.focus();
  };

  // Handle clear search
  const handleClearSearch = () => {
    setInputValue('');
    onSearch?.('');
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    inputRef?.current?.focus();
  };

  return (
    <div className="relative">
      {/* Search Form */}
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon name="Search" size={20} className="text-muted-foreground" />
          </div>
          
          <input
            ref={inputRef}
            type="text"
            placeholder="Rechercher des artistes, agents, DJ, musiciens..."
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            className={cn(
              "flex h-12 w-full rounded-lg border border-input bg-background pl-10 pr-20 py-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
              "text-base md:text-sm" // Larger text on mobile for better UX
            )}
            disabled={isLoading}
          />
          
          {/* Clear Button */}
          {inputValue && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute inset-y-0 right-16 flex items-center pr-2 hover:bg-muted/50 rounded-md transition-colors"
              disabled={isLoading}
            >
              <Icon name="X" size={18} className="text-muted-foreground hover:text-foreground" />
            </button>
          )}
          
          {/* Search Button */}
          <div className="absolute inset-y-0 right-0 flex items-center">
            <Button
              type="submit"
              size="sm"
              className="mr-1 h-10 px-4"
              disabled={isLoading}
              loading={isLoading}
            >
              {!isLoading && <Icon name="Search" size={16} />}
              <span className="sr-only">Rechercher</span>
            </Button>
          </div>
        </div>
      </form>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions?.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute top-full left-0 right-0 z-50 mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto"
        >
          {suggestions?.map((suggestion, index) => (
            <button
              key={`${suggestion}-${index}`}
              type="button"
              className={cn(
                "w-full px-4 py-3 text-left text-sm hover:bg-accent hover:text-accent-foreground transition-colors border-b border-border last:border-b-0",
                selectedSuggestionIndex === index && "bg-accent text-accent-foreground"
              )}
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <div className="flex items-center space-x-3">
                <Icon 
                  name={index === 0 && suggestion === inputValue ? "Search" : "Clock"} 
                  size={16} 
                  className="text-muted-foreground flex-shrink-0" 
                />
                <span className="flex-1 truncate">{suggestion}</span>
                {index === 0 && suggestion === inputValue && (
                  <span className="text-xs text-muted-foreground">Rechercher</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Quick Search Suggestions */}
      <div className="mt-4 hidden sm:block">
        <p className="text-sm text-muted-foreground mb-2">Suggestions populaires:</p>
        <div className="flex flex-wrap gap-2">
          {['Artistes Paris', 'DJ événements', 'Musiciens jazz', 'Agents booking', 'Chanteurs pop']?.map((suggestion) => (
            <Button
              key={suggestion}
              variant="outline"
              size="xs"
              onClick={() => handleSuggestionClick(suggestion)}
              className="text-xs"
            >
              {suggestion}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SearchBar;